import React, { useEffect, useState, useRef } from 'react';
import { ImageCropProps } from '../types';
import { useImageProcessor } from '../hooks/useImageProcessor';

const ImageCrop: React.FC<ImageCropProps> = ({
    image,
    width,
    height,
    aspectRatio,
    cropX = 0,
    cropY = 0,
    allowResize = true,
    showGrid = true,
    gridColor = 'rgba(255, 255, 255, 0.8)',
    onProcessingComplete,
    onError,
    className,
    style,
}) => {
    const [processedImageUrl, setProcessedImageUrl] = useState<string | null>(null);
    const [cropArea, setCropArea] = useState({
        x: cropX,
        y: cropY,
        width: width,
        height: height,
    });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
    const [resizing, setResizing] = useState(false);
    const [resizeDirection, setResizeDirection] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    const { imageBlob, imageUrl, isLoading, dimensions, cropImage } = useImageProcessor({
        image,
        onError,
    });

    // Initialize crop area when dimensions are available
    useEffect(() => {
        if (dimensions) {
            const initialWidth = width > dimensions.width ? dimensions.width : width;
            const initialHeight = height > dimensions.height ? dimensions.height : height;

            // If aspectRatio is provided, adjust the height
            const finalHeight = aspectRatio ? initialWidth / aspectRatio : initialHeight;

            setCropArea({
                x: cropX,
                y: cropY,
                width: initialWidth,
                height: finalHeight,
            });
        }
    }, [dimensions, width, height, aspectRatio, cropX, cropY]);

    // Process the crop
    useEffect(() => {
        const processCrop = async () => {
            if (imageBlob && !isDragging && !resizing) {
                try {
                    const croppedBlob = await cropImage(
                        cropArea.x,
                        cropArea.y,
                        cropArea.width,
                        cropArea.height
                    );
                    const croppedUrl = URL.createObjectURL(croppedBlob);

                    setProcessedImageUrl(croppedUrl);

                    if (onProcessingComplete) {
                        onProcessingComplete(croppedBlob);
                    }
                } catch (err) {
                    console.error('Error cropping image:', err);
                    if (onError && err instanceof Error) {
                        onError(err);
                    }
                }
            }
        };

        processCrop();

        // Cleanup function
        return () => {
            if (processedImageUrl) {
                URL.revokeObjectURL(processedImageUrl);
            }
        };
    }, [imageBlob, cropArea, isDragging, resizing, onProcessingComplete, onError, cropImage]);

    // Mouse event handlers for moving the crop area
    const handleMouseDown = (e: React.MouseEvent) => {
        if (!dimensions || !containerRef.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const scaleX = dimensions.width / containerRect.width;
        const scaleY = dimensions.height / containerRect.height;

        const mouseX = (e.clientX - containerRect.left) * scaleX;
        const mouseY = (e.clientY - containerRect.top) * scaleY;

        // Check if clicking on a resize handle
        if (allowResize) {
            const handleSize = 10;
            const isOnRightEdge = Math.abs(mouseX - (cropArea.x + cropArea.width)) < handleSize;
            const isOnBottomEdge = Math.abs(mouseY - (cropArea.y + cropArea.height)) < handleSize;
            const isOnLeftEdge = Math.abs(mouseX - cropArea.x) < handleSize;
            const isOnTopEdge = Math.abs(mouseY - cropArea.y) < handleSize;

            if (isOnRightEdge && isOnBottomEdge) {
                setResizeDirection('se');
                setResizing(true);
                setDragStartPos({ x: mouseX, y: mouseY });
                return;
            } else if (isOnLeftEdge && isOnBottomEdge) {
                setResizeDirection('sw');
                setResizing(true);
                setDragStartPos({ x: mouseX, y: mouseY });
                return;
            } else if (isOnRightEdge && isOnTopEdge) {
                setResizeDirection('ne');
                setResizing(true);
                setDragStartPos({ x: mouseX, y: mouseY });
                return;
            } else if (isOnLeftEdge && isOnTopEdge) {
                setResizeDirection('nw');
                setResizing(true);
                setDragStartPos({ x: mouseX, y: mouseY });
                return;
            } else if (isOnRightEdge) {
                setResizeDirection('e');
                setResizing(true);
                setDragStartPos({ x: mouseX, y: mouseY });
                return;
            } else if (isOnBottomEdge) {
                setResizeDirection('s');
                setResizing(true);
                setDragStartPos({ x: mouseX, y: mouseY });
                return;
            } else if (isOnLeftEdge) {
                setResizeDirection('w');
                setResizing(true);
                setDragStartPos({ x: mouseX, y: mouseY });
                return;
            } else if (isOnTopEdge) {
                setResizeDirection('n');
                setResizing(true);
                setDragStartPos({ x: mouseX, y: mouseY });
                return;
            }
        }

        // Check if clicking inside crop area to move it
        if (
            mouseX >= cropArea.x &&
            mouseX <= cropArea.x + cropArea.width &&
            mouseY >= cropArea.y &&
            mouseY <= cropArea.y + cropArea.height
        ) {
            setIsDragging(true);
            setDragStartPos({ x: mouseX - cropArea.x, y: mouseY - cropArea.y });
        }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!containerRef.current || !dimensions) return;

        const container = containerRef.current;
        const rect = container.getBoundingClientRect();
        const scale = dimensions.width / rect.width;

        const mouseX = (e.clientX - rect.left) * scale;
        const mouseY = (e.clientY - rect.top) * scale;

        if (resizing) {
            updateCropAreaWithResize(e);
        } else if (isDragging) {
            const newX = mouseX - dragStartPos.x;
            const newY = mouseY - dragStartPos.y;

            // Constrain to image boundaries
            const constrainedX = Math.max(0, Math.min(dimensions.width - cropArea.width, newX));
            const constrainedY = Math.max(0, Math.min(dimensions.height - cropArea.height, newY));

            setCropArea(prev => ({
                ...prev,
                x: constrainedX,
                y: constrainedY,
            }));
        }
    };

    const handleResize = (e: React.MouseEvent<HTMLDivElement>, direction: string) => {
        if (!allowResize || !containerRef.current || !dimensions) return;

        e.preventDefault();
        setResizing(true);
        setResizeDirection(direction);
    };

    const updateCropAreaWithResize = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!resizing || !containerRef.current || !dimensions) return;

        const container = containerRef.current;
        const rect = container.getBoundingClientRect();
        const scale = dimensions.width / rect.width;

        const mouseX = (e.clientX - rect.left) * scale;
        const mouseY = (e.clientY - rect.top) * scale;

        let newX = cropArea.x;
        let newY = cropArea.y;
        let newWidth = cropArea.width;
        let newHeight = cropArea.height;

        // Handle different resize directions
        if (resizeDirection.includes('e')) {
            newWidth = mouseX - cropArea.x;
        }
        if (resizeDirection.includes('w')) {
            const widthChange = cropArea.x - mouseX;
            newX = mouseX;
            newWidth = cropArea.width + widthChange;
        }
        if (resizeDirection.includes('s')) {
            newHeight = mouseY - cropArea.y;
        }
        if (resizeDirection.includes('n')) {
            const heightChange = cropArea.y - mouseY;
            newY = mouseY;
            newHeight = cropArea.height + heightChange;
        }

        // Ensure minimum size and within image boundaries
        const minSize = 20;
        newWidth = Math.max(minSize, newWidth);
        newHeight = aspectRatio ? newWidth / aspectRatio : Math.max(minSize, newHeight);

        // Fix for TypeScript error with aspectRatio
        if (aspectRatio && (resizeDirection.includes('n') || resizeDirection.includes('s'))) {
            newWidth = newHeight * aspectRatio;
        }

        // Constrain to image boundaries
        if (newX < 0) {
            newX = 0;
            if (['w', 'nw', 'sw'].includes(resizeDirection)) {
                newWidth = cropArea.x + cropArea.width;
                if (aspectRatio) newHeight = newWidth / aspectRatio;
            }
        }

        if (newY < 0) {
            newY = 0;
            if (['n', 'nw', 'ne'].includes(resizeDirection)) {
                newHeight = cropArea.y + cropArea.height;
                if (aspectRatio) newWidth = newHeight * aspectRatio;
            }
        }

        if (newX + newWidth > dimensions.width) {
            newWidth = dimensions.width - newX;
            if (aspectRatio) newHeight = newWidth / aspectRatio;
        }

        if (newY + newHeight > dimensions.height) {
            newHeight = dimensions.height - newY;
            if (aspectRatio) newWidth = newHeight * aspectRatio;
        }

        setCropArea({
            x: newX,
            y: newY,
            width: newWidth,
            height: newHeight,
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        setResizing(false);
    };

    if (isLoading) {
        return <div className={className} style={style}>Loading image...</div>;
    }

    return (
        <div
            className={className}
            style={{
                ...style,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <div
                ref={containerRef}
                style={{
                    position: 'relative',
                    maxWidth: '100%',
                    cursor: isDragging ? 'grabbing' : (resizing ? 'nwse-resize' : 'default'),
                    width: dimensions ? `${dimensions.width}px` : 'auto',
                    height: dimensions ? `${dimensions.height}px` : 'auto',
                    overflow: 'hidden',
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                {imageUrl && (
                    <>
                        <img
                            src={imageUrl}
                            alt="Original"
                            style={{
                                maxWidth: '100%',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                            }}
                        />

                        {/* Crop overlay */}
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                pointerEvents: 'none',
                                clipPath: `polygon(
                  0 0, 100% 0, 100% 100%, 0 100%,
                  0 0,
                  ${cropArea.x}px ${cropArea.y}px,
                  ${cropArea.x}px ${cropArea.y + cropArea.height}px,
                  ${cropArea.x + cropArea.width}px ${cropArea.y + cropArea.height}px,
                  ${cropArea.x + cropArea.width}px ${cropArea.y}px,
                  ${cropArea.x}px ${cropArea.y}px
                )`,
                            }}
                        />

                        {/* Crop selection */}
                        <div
                            style={{
                                position: 'absolute',
                                top: `${cropArea.y}px`,
                                left: `${cropArea.x}px`,
                                width: `${cropArea.width}px`,
                                height: `${cropArea.height}px`,
                                border: `2px solid ${gridColor}`,
                                boxSizing: 'border-box',
                                pointerEvents: 'none',
                            }}
                        >
                            {/* Grid lines */}
                            {showGrid && (
                                <>
                                    <div style={{
                                        position: 'absolute',
                                        left: '33.33%',
                                        top: 0,
                                        height: '100%',
                                        width: '1px',
                                        backgroundColor: gridColor,
                                    }} />
                                    <div style={{
                                        position: 'absolute',
                                        left: '66.66%',
                                        top: 0,
                                        height: '100%',
                                        width: '1px',
                                        backgroundColor: gridColor,
                                    }} />
                                    <div style={{
                                        position: 'absolute',
                                        top: '33.33%',
                                        left: 0,
                                        width: '100%',
                                        height: '1px',
                                        backgroundColor: gridColor,
                                    }} />
                                    <div style={{
                                        position: 'absolute',
                                        top: '66.66%',
                                        left: 0,
                                        width: '100%',
                                        height: '1px',
                                        backgroundColor: gridColor,
                                    }} />
                                </>
                            )}

                            {/* Resize handles */}
                            {allowResize && (
                                <>
                                    <div style={{
                                        position: 'absolute',
                                        right: '-5px',
                                        bottom: '-5px',
                                        width: '10px',
                                        height: '10px',
                                        backgroundColor: 'white',
                                        border: '1px solid black',
                                        borderRadius: '50%',
                                        cursor: 'nwse-resize',
                                        pointerEvents: 'auto',
                                    }} />
                                    <div style={{
                                        position: 'absolute',
                                        left: '-5px',
                                        bottom: '-5px',
                                        width: '10px',
                                        height: '10px',
                                        backgroundColor: 'white',
                                        border: '1px solid black',
                                        borderRadius: '50%',
                                        cursor: 'nesw-resize',
                                        pointerEvents: 'auto',
                                    }} />
                                    <div style={{
                                        position: 'absolute',
                                        right: '-5px',
                                        top: '-5px',
                                        width: '10px',
                                        height: '10px',
                                        backgroundColor: 'white',
                                        border: '1px solid black',
                                        borderRadius: '50%',
                                        cursor: 'nesw-resize',
                                        pointerEvents: 'auto',
                                    }} />
                                    <div style={{
                                        position: 'absolute',
                                        left: '-5px',
                                        top: '-5px',
                                        width: '10px',
                                        height: '10px',
                                        backgroundColor: 'white',
                                        border: '1px solid black',
                                        borderRadius: '50%',
                                        cursor: 'nwse-resize',
                                        pointerEvents: 'auto',
                                    }} />
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>

            <div style={{ marginTop: '1rem', width: '100%' }}>
                <h3>Preview</h3>
                <div style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '0.5rem',
                }}>
                    {processedImageUrl && (
                        <img
                            src={processedImageUrl}
                            alt="Cropped"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '300px',
                                border: '1px solid #ddd',
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImageCrop; 